export class ObjectUtils {
	/**
	 * Safely traverse through an object with a dot notated path string
	 * @param path
	 * @param obj
	 */
	public static resolveDotNotationPath(path, obj) {
		return path?.split('.').reduce((prev, curr) => (prev ? prev[curr] : undefined), obj || self);
	}

	public static resolvePath(path: string, obj: any): any {
		if (!path) return undefined;

		// 1) Convert bracket notation of the form:
		//      ["some property"]  =>  .some property
		//      ['some property']  =>  .some property
		//    This ensures that afterwards we only deal with dot-notation
		const normalizedPath = path
		  // bracket with single quotes -> .group1
		  .replace(/\['([^']*)'\]/g, '.$1')
		  // bracket with double quotes -> .group1
		  .replace(/\["([^"]*)"\]/g, '.$1');

		// 2) Split on '.' to get the property segments
		//    and filter out any accidental empty strings
		//    (in case the path starts or ends with '.')
		const segments = normalizedPath
		  .split('.')
		  .filter(Boolean);

		// 3) Traverse the object using the segments
		return segments.reduce((prev, curr) => {
		  if (prev === undefined || prev === null) return undefined;
		  return prev[curr];
		}, obj);
	}

	public static createFromDotNotationPath(path) {
		return path.split('.').reduceRight((prev, curr) => ({ [curr]: prev }), {});
	}

	public static unfreeze(obj: any) {
		return JSON.parse(JSON.stringify(obj));
	}

	public static getPropertyByName(input: Object | any[], keyName: string) {
		for (const [k, v] of Object.entries(input)) {
			if (k === keyName) {
				return v;
			}
			if (Array.isArray(v)) {
				const result = this.getPropertyByName(v, keyName);
				if (result !== -1) {
					return result;
				}
			}
			if (typeof v === 'object') {
				const result = this.getPropertyByName(v, keyName);
				if (result !== -1) {
					return result;
				}
			}
		}
		return -1;
	}

	public static cleanObject(obj, includeEmptyStrings = false) {
		// If the input is not an object, return it as it is
		if (typeof obj !== 'object' || obj === null) {
			return obj;
		}

		// Iterate over each property of the object
		Object.keys(obj).forEach(key => {
			// Recursively clean the nested object
			obj[key] = this.cleanObject(obj[key]);

			// If the cleaned value is an object and is either empty or has only undefined values
			if (typeof obj[key] === 'object' && obj[key] !== null) {
				// Check if the object is empty or all properties are undefined
				const isEmpty = Object.keys(obj[key]).length === 0;
				// const allUndefined = Object.values(obj[key]).every(value => value === undefined);
				const allUndefinedOrEmptyString = Object.values(obj[key]).every(
					value => value === undefined || (value === '' && !includeEmptyStrings)
				);

				// If the object is empty or all properties are undefined, delete the property
				if (isEmpty || allUndefinedOrEmptyString) {
					delete obj[key];
				}
			}
		});

		return obj;
	}

	public static getLocale(value: string, localeData: any): string {
		let locale = window.navigator.language;
		if (localStorage.getItem('localeCode')) {
			locale = localStorage.getItem('localeCode');
		}

		// See if the localeData has a value for the current locale.
		if (localeData && localeData[locale]) {
			return localeData[locale] as string; // Force to string, becuase we should have passed the correct type.
		} else {
			return value;
		}
	}
}
